/**
 * DriveClientMergePanel - Fusion des anciens dossiers "Client - X" dans le dossier projet.
 *
 * Pour chaque dossier client global (collection client_drive_folders) :
 *  - si un projet est lie -> deplace tous les fichiers vers projet/CLIENT/01_Pieces_justificatives,
 *    puis supprime le dossier client vide.
 *  - sinon -> orphelin, listee a part. Suppression optionnelle (case a cocher).
 *
 * Reserve aux CEO / Admin.
 */
import React, { useCallback, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriveClientMergePanel = () => {
  const [plan, setPlan] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedLinked, setSelectedLinked] = useState({});
  const [selectedOrphans, setSelectedOrphans] = useState({});
  const [deleteOrphans, setDeleteOrphans] = useState(false);
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/drive/client-merge/preview`);
      setPlan(r.data);
      const linked = {};
      (r.data.linked || []).forEach((it) => { linked[it.lead_id] = true; });
      setSelectedLinked(linked);
      setSelectedOrphans({});
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoadingPreview(false); }
  }, []);

  const toggleAllLinked = (val) => {
    if (!plan?.linked) return;
    const next = {};
    plan.linked.forEach((it) => { next[it.lead_id] = val; });
    setSelectedLinked(next);
  };

  const execute = async () => {
    if (!plan) return;
    const linkedIds = Object.keys(selectedLinked).filter((k) => selectedLinked[k]);
    const orphanIds = deleteOrphans
      ? Object.keys(selectedOrphans).filter((k) => selectedOrphans[k])
      : [];
    const allIds = [...linkedIds, ...orphanIds];
    if (allIds.length === 0) {
      alert('Selectionnez au moins un dossier client a traiter.');
      return;
    }
    let msg = '';
    if (linkedIds.length) msg += `${linkedIds.length} dossier(s) client a fusionner dans leur projet.\n`;
    if (deleteOrphans && orphanIds.length) msg += `${orphanIds.length} dossier(s) ORPHELIN(S) seront SUPPRIMES.\n`;
    msg += '\nCette operation est IRREVERSIBLE. Confirmer ?';
    if (!window.confirm(msg)) return;

    setExecuting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/drive/client-merge/execute`, {
        lead_ids: allIds,
        delete_emptied_folders: true,
        delete_orphans: deleteOrphans,
      });
      setResult(r.data);
      setTimeout(() => loadPreview(), 800);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setExecuting(false); }
  };

  return (
    <div className="realease-card p-6" data-testid="drive-client-merge-panel">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
          Fusionner les dossiers Client dans les projets
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Pour chaque <code className="bg-gray-100 px-1 rounded">Client - X</code> avec un projet lie, deplace TOUS les fichiers vers
          <code className="bg-gray-100 px-1 rounded mx-1">VILLE - NOMS / CLIENT / 01_Pieces_justificatives</code>
          puis supprime le dossier client vide. Les orphelins (sans projet) sont listes separement.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="px-4 py-2 rounded-md text-sm font-semibold bg-[#A57F4F] text-white hover:bg-[#8d6a40] disabled:opacity-50 transition"
          data-testid="drive-merge-preview-btn"
        >
          {loadingPreview ? 'Analyse...' : 'Analyser les dossiers client'}
        </button>
        {plan && (plan.linked_count > 0 || plan.orphan_count > 0) && (
          <button
            type="button"
            onClick={execute}
            disabled={executing}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
            data-testid="drive-merge-execute-btn"
          >
            {executing ? 'Fusion en cours...' : 'Lancer la fusion'}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="drive-merge-error">
          Erreur : {error}
        </div>
      )}

      {plan && (
        <div className="space-y-4">
          {/* Linked clients (a fusionner) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                Dossiers a fusionner ({plan.linked_count})
              </p>
              {plan.linked_count > 0 && (
                <div className="flex gap-2">
                  <button onClick={() => toggleAllLinked(true)} className="text-xs text-[#13736D] hover:underline" data-testid="drive-merge-linked-select-all">Tout</button>
                  <button onClick={() => toggleAllLinked(false)} className="text-xs text-gray-500 hover:underline" data-testid="drive-merge-linked-deselect-all">Rien</button>
                </div>
              )}
            </div>
            {plan.linked_count === 0 ? (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                Aucun dossier client a fusionner. Tous les dossiers sont deja dans leur projet ou orphelins.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 w-6"></th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Dossier client</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Projet cible</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Destination</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.linked.map((it) => (
                      <tr key={it.lead_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selectedLinked[it.lead_id]}
                            onChange={(e) => setSelectedLinked({ ...selectedLinked, [it.lead_id]: e.target.checked })}
                            data-testid={`drive-merge-check-${it.lead_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-gray-600 font-mono">{it.client_folder_name}</td>
                        <td className="px-2 py-2 text-[#13736D] font-semibold">{it.project_name || it.project_id}</td>
                        <td className="px-2 py-2 text-gray-500 text-[11px]">{it.target_path || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Orphans */}
          {plan.orphan_count > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">
                  Dossiers orphelins ({plan.orphan_count})
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteOrphans}
                    onChange={(e) => setDeleteOrphans(e.target.checked)}
                    data-testid="drive-merge-delete-orphans-toggle"
                  />
                  Supprimer les orphelins selectionnes
                </label>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">
                Ces dossiers n&apos;ont pas de projet associe (lead sans projet ou typo). Cochez ceux a supprimer.
              </p>
              <div className="overflow-x-auto border border-amber-200 rounded-lg bg-amber-50/30">
                <table className="w-full text-xs">
                  <thead className="bg-amber-50 border-b border-amber-200">
                    <tr>
                      <th className="px-2 py-2 w-6"></th>
                      <th className="px-2 py-2 text-left font-semibold text-amber-800">Dossier orphelin</th>
                      <th className="px-2 py-2 text-left font-semibold text-amber-800">Lead ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.orphans.map((it) => (
                      <tr key={it.lead_id} className="border-b border-amber-100 last:border-b-0 hover:bg-amber-50/50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            disabled={!deleteOrphans}
                            checked={!!selectedOrphans[it.lead_id]}
                            onChange={(e) => setSelectedOrphans({ ...selectedOrphans, [it.lead_id]: e.target.checked })}
                            data-testid={`drive-merge-orphan-check-${it.lead_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-amber-800">{it.client_folder_name || '—'}</td>
                        <td className="px-2 py-2 text-gray-500 font-mono text-[10px]">{it.lead_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 rounded-md bg-emerald-50 border border-emerald-200" data-testid="drive-merge-result">
          <p className="font-semibold text-emerald-800 mb-2">
            Fusion terminee - {result.processed} dossier(s) traite(s).
          </p>
          <ul className="text-xs text-emerald-700 space-y-0.5 max-h-60 overflow-y-auto">
            {(result.results || []).map((r) => (
              <li key={r.lead_id}>
                {r.ok ? '✓' : '✗'} {r.lead_id}
                {r.action === 'deleted_orphan' && ' (orphelin supprime)'}
                {r.action === 'skipped_orphan' && ' (orphelin conserve)'}
                {typeof r.moved_files === 'number' && ` (${r.moved_files} fichier(s) deplace(s), ${r.deleted_folder ? 'dossier supprime' : 'dossier conserve'}, projet: ${r.project_name || r.project_id})`}
                {r.error && ` (${r.error})`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DriveClientMergePanel;
